/**
 * Unified marketing / product-analytics tag layer.
 *
 * One place that loads and feeds every third-party measurement tool we use:
 *
 *   - Google Tag Manager       REACT_APP_GTM_ID            (GTM-XXXXXXX)
 *   - Google Analytics 4       REACT_APP_GA_MEASUREMENT_ID (G-XXXXXXXXXX)
 *   - Google Ads pixel         REACT_APP_GOOGLE_ADS_ID     (AW-XXXXXXXXX)
 *   - Google Ads conversion    REACT_APP_GOOGLE_ADS_ENQUIRY_LABEL
 *   - Meta pixel (optional)    REACT_APP_FACEBOOK_PIXEL_ID
 *   - PostHog                  REACT_APP_POSTHOG_KEY / REACT_APP_POSTHOG_HOST
 *
 * Everything is optional: with no IDs configured nothing is loaded and every
 * function is a silent no-op, so local development and forks stay untracked.
 *
 * Consent rules (GDPR / Kenya DPA):
 *   analytics consent -> GA4, GTM, PostHog
 *   marketing consent -> Google Ads / Meta advertising signals
 * Google Consent Mode v2 defaults are set to "denied" before any tag loads and
 * updated the moment the visitor answers the cookie banner.
 */

import { getConsent, CONSENT_EVENT } from "./cookieConsent";

const env = (key) => {
  const value = process.env[key];
  return value && String(value).trim() ? String(value).trim() : "";
};

export const TAG_CONFIG = {
  gtmId: env("REACT_APP_GTM_ID"),
  ga4Id: env("REACT_APP_GA_MEASUREMENT_ID"),
  googleAdsId: env("REACT_APP_GOOGLE_ADS_ID"),
  googleAdsEnquiryLabel: env("REACT_APP_GOOGLE_ADS_ENQUIRY_LABEL"),
  facebookPixelId: env("REACT_APP_FACEBOOK_PIXEL_ID"),
  posthogKey: env("REACT_APP_POSTHOG_KEY"),
  posthogHost: env("REACT_APP_POSTHOG_HOST") || "https://eu.i.posthog.com",
};

/** Which tools are configured — used by the admin "tag health" panel. */
export function getTagStatus() {
  return [
    { key: "gtm", name: "Google Tag Manager", id: TAG_CONFIG.gtmId, needs: "REACT_APP_GTM_ID" },
    { key: "ga4", name: "Google Analytics 4", id: TAG_CONFIG.ga4Id, needs: "REACT_APP_GA_MEASUREMENT_ID" },
    { key: "ads", name: "Google Ads pixel", id: TAG_CONFIG.googleAdsId, needs: "REACT_APP_GOOGLE_ADS_ID" },
    { key: "meta", name: "Meta pixel", id: TAG_CONFIG.facebookPixelId, needs: "REACT_APP_FACEBOOK_PIXEL_ID" },
    { key: "posthog", name: "PostHog", id: TAG_CONFIG.posthogKey ? "connected" : "", needs: "REACT_APP_POSTHOG_KEY" },
  ].map((tag) => ({ ...tag, active: Boolean(tag.id) }));
}

function consentState() {
  const consent = getConsent();
  return {
    analytics: Boolean(consent && consent.analytics),
    marketing: Boolean(consent && consent.marketing),
  };
}

// ------------------------------------------------------------------
// Google consent mode + gtag bootstrap
// ------------------------------------------------------------------

function ensureGtag() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
  return window.gtag;
}

let consentDefaultsSet = false;
function setConsentDefaults() {
  if (consentDefaultsSet) return;
  const gtag = ensureGtag();
  if (!gtag) return;
  consentDefaultsSet = true;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
}

function pushConsentUpdate() {
  const gtag = ensureGtag();
  if (!gtag) return;
  const { analytics, marketing } = consentState();
  gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  });
}

function injectScript(id, src, attrs = {}) {
  if (typeof document === "undefined") return null;
  if (document.getElementById(id)) return document.getElementById(id);
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  Object.entries(attrs).forEach(([k, v]) => script.setAttribute(k, v));
  document.head.appendChild(script);
  return script;
}

// ------------------------------------------------------------------
// Individual loaders
// ------------------------------------------------------------------

let gtmLoaded = false;
function loadGtm() {
  if (gtmLoaded || !TAG_CONFIG.gtmId) return;
  gtmLoaded = true;
  ensureGtag();
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  injectScript("gtm-script", `https://www.googletagmanager.com/gtm.js?id=${TAG_CONFIG.gtmId}`);
}

let googleTagLoaded = false;
function loadGoogleTag() {
  const primary = TAG_CONFIG.ga4Id || TAG_CONFIG.googleAdsId;
  if (!primary) return;
  const gtag = ensureGtag();
  if (!googleTagLoaded) {
    googleTagLoaded = true;
    injectScript("gtag-script", `https://www.googletagmanager.com/gtag/js?id=${primary}`);
    gtag("js", new Date());
  }
  if (TAG_CONFIG.ga4Id) {
    gtag("config", TAG_CONFIG.ga4Id, { anonymize_ip: true, send_page_view: false });
  }
  if (TAG_CONFIG.googleAdsId) {
    gtag("config", TAG_CONFIG.googleAdsId, { allow_enhanced_conversions: true });
  }
}

let metaLoaded = false;
function loadMetaPixel() {
  if (metaLoaded || !TAG_CONFIG.facebookPixelId || typeof document === "undefined") return;
  metaLoaded = true;
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = true; t.id = "meta-pixel-script"; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", TAG_CONFIG.facebookPixelId);
}

let posthogLoaded = false;
function loadPostHog() {
  if (posthogLoaded || !TAG_CONFIG.posthogKey || typeof window === "undefined") return;
  posthogLoaded = true;
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r; e.__SV || ((window.posthog = e), (e._i = []), (e.init = function (i, s, a) {
      function g(t, e) { var o = e.split("."); 2 == o.length && ((t = t[o[0]]), (e = o[1])); t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; }
      ((p = t.createElement("script")).type = "text/javascript"), (p.async = !0), (p.id = "posthog-script"), (p.src = s.api_host + "/static/array.js");
      (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
      var u = e; for (void 0 !== a ? (u = e[a] = []) : (a = "posthog"), u.people = u.people || [], u.toString = function (t) { var e = "posthog"; return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e; }, u.people.toString = function () { return u.toString(1) + ".people (stub)"; }, o = "capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys".split(" "), n = 0; n < o.length; n++) g(u, o[n]);
      e._i.push([i, s, a]);
    }), (e.__SV = 1));
  })(document, window.posthog || []);
  /* eslint-enable */
  window.posthog.init(TAG_CONFIG.posthogKey, {
    api_host: TAG_CONFIG.posthogHost,
    capture_pageview: false,
    persistence: "localStorage+cookie",
    autocapture: true,
  });
}

// ------------------------------------------------------------------
// Public API
// ------------------------------------------------------------------

let initialised = false;

/** Loads every configured tag that current consent allows. Safe to call often. */
export function initMarketingTags() {
  if (typeof window === "undefined") return;
  setConsentDefaults();

  if (!initialised) {
    initialised = true;
    window.addEventListener(CONSENT_EVENT, () => {
      pushConsentUpdate();
      initMarketingTags();
    });
  }

  const { analytics, marketing } = consentState();
  pushConsentUpdate();

  if (analytics) {
    loadGtm();
    loadGoogleTag();
    loadPostHog();
  }
  if (marketing) {
    loadGoogleTag();
    loadMetaPixel();
  }
}

/** Page view for GA4, GTM and PostHog (SPA route changes included). */
export function tagPageView(path, title) {
  if (typeof window === "undefined") return;
  const pagePath = path || window.location.pathname + window.location.search;
  const pageTitle = title || document.title;

  if (window.dataLayer) {
    window.dataLayer.push({ event: "page_view", page_path: pagePath, page_title: pageTitle });
  }
  if (window.gtag && TAG_CONFIG.ga4Id) {
    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: window.location.href,
    });
  }
  if (window.posthog && window.posthog.capture) {
    window.posthog.capture("$pageview", { $current_url: window.location.href, path: pagePath, title: pageTitle });
  }
  if (window.fbq) window.fbq("track", "PageView");
}

/** Any custom business event — mirrored to GA4, GTM, PostHog and Meta. */
export function tagEvent(name, params = {}) {
  if (typeof window === "undefined" || !name) return;
  const payload = { ...params };

  if (window.dataLayer) window.dataLayer.push({ event: name, ...payload });
  if (window.gtag && (TAG_CONFIG.ga4Id || TAG_CONFIG.googleAdsId)) window.gtag("event", name, payload);
  if (window.posthog && window.posthog.capture) window.posthog.capture(name, payload);
  if (window.fbq) window.fbq("trackCustom", name, payload);
}

/** Fires the Google Ads conversion + Meta Lead when an enquiry is submitted. */
export function tagEnquiryConversion(params = {}) {
  if (typeof window === "undefined") return;
  tagEvent("generate_lead", { currency: "KES", value: 1, ...params });
  if (window.gtag && TAG_CONFIG.googleAdsId && TAG_CONFIG.googleAdsEnquiryLabel) {
    window.gtag("event", "conversion", {
      send_to: `${TAG_CONFIG.googleAdsId}/${TAG_CONFIG.googleAdsEnquiryLabel}`,
      value: 1,
      currency: "KES",
    });
  }
  if (window.fbq) window.fbq("track", "Lead");
}

/** Attaches a stable visitor id so PostHog/GA sessions stitch together. */
export function identifyVisitor(deviceId, traits = {}) {
  if (typeof window === "undefined" || !deviceId) return;
  if (window.posthog && window.posthog.identify) window.posthog.identify(deviceId, traits);
  if (window.gtag && TAG_CONFIG.ga4Id) window.gtag("set", { user_id: deviceId });
}
